import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Observable, Subscription, forkJoin, of } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { MessageService } from 'primeng/api';

// Services
import { TotalDashboardService } from '../../services/dashboard/total-dashboard.service';
import { DashboardService } from '../../services/dashboard/dashboard.service';
import { AvisosService } from '../../services/dashboard/avisos.service';
import { ContasPagarReceberService } from '../../services/dashboard/contas-pagar-receber.service';
import { ContaService } from '../../services/conta/conta.service';
import { ProdutosService } from '../../services/produtos/produto.service';
import { EmpresaConfigService } from '../../services/configuracoes/empresa.service';

// Models
import { TotalDashboard } from '../../models/total-dashboard.model';
import { ContaDTO, ContaResponseDTO, MoedaBackend } from '../../models/gerenciar-contas/conta.model';
import { ProdutosDTO } from '../../models/produto.models';
import { PeriodoFiltro } from '../../models/dashboard/periodo-filtro.model';
import { AvisoDTO } from '../../models/dashboard/aviso.model';
import { ConfiguracaoEmpresaDashboardDTO } from '../../models/dashboard/configuracao-empresa-dashboard.model';
import { ContaPagarReceberDTO } from '../../models/dashboard/conta-pagar-receber.model';
import { TotaisContasDTO, ValorPorMoedaDTO } from '../../models/dashboard/totais-contas.model';
import { DreItemDTO, DadosGraficoPizza } from '../../models/dashboard/dre.model';
import { Moeda, MOEDA_CONFIG } from '../../enums/moeda.enum';
import { ModuloPermissaoDashboard, PERMISSAO_TOTAL } from '../../models/dashboard/modulo-permissao.model';
import { PermissaoDashboardService } from '../../services/dashboard/permissao-dashboard.service';
import { fmtDateTime } from '../../shared/utils/periodo.utils';

// Paleta exata do mobile (conta_controller.dart getCor())
const PALETA_CORES = [
  '#4F81BD', '#F79646', '#9BBB59', '#C0504D',
  '#8064A2', '#FFCC00', '#4BACC6', '#A5A5A5'
];

@Component({
  selector: 'app-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss'],
  providers: [MessageService]
})
export class DashboardComponent implements OnInit, OnDestroy {

  // Permissoes por modulo (replica moduloDePermissao do mobile)
  permissoes: ModuloPermissaoDashboard = { ...PERMISSAO_TOTAL };

  // Estado de carregamento
  carregando = false;
  carregandoFinanceiro = false;

  // Configuracao da empresa
  configuracaoEmpresa: ConfiguracaoEmpresaDashboardDTO | undefined;
  empresaId = 0;

  // Moeda global (cabecalho, grafico de linha, pizza, minhas contas)
  currencySymbol = 'PYG';
  currencyDigits = '1.0-0';
  currencyLocale = 'es-PY';

  // Moeda independente por card de contas a pagar/receber
  currencySymbolPagar = 'PYG';
  currencyDigitsPagar = '1.0-0';
  currencyLocalePagar = 'es-PY';
  currencySymbolReceber = 'PYG';
  currencyDigitsReceber = '1.0-0';
  currencyLocaleReceber = 'es-PY';

  // Periodo e conta
  periodoSelecionado!: PeriodoFiltro;
  contaSelecionada: ContaDTO | null = null;
  contas: ContaDTO[] = [];

  // Cabecalho
  totalReceita = 0;
  totalDespesa = 0;
  totalSaldo = 0;

  // KPI: dados do mes anterior para calculo de tendencia
  totalReceitaMesAnterior = 0;
  totalDespesaMesAnterior = 0;
  totalSaldoMesAnterior = 0;
  periodoEhMesFechado = true;

  // Avisos
  avisos: AvisoDTO[] = [];

  // Plano
  totalVendasNoMes = 0;

  // Primeiros passos
  temProduto = false;
  temCliente = false;
  temVenda = false;

  // Fatura eletronica
  mostrarPassosFaturaEletronica = false;
  temDadosEmpresa = false;
  temCertificadoDigital = false;
  temTimbradoEletronico = false;
  temVendaTimbrado = false;

  // Contas a pagar/receber
  contasAPagar: ContaPagarReceberDTO[] = [];
  contasAReceber: ContaPagarReceberDTO[] = [];
  totalAPagar = 0;
  totalPagos = 0;
  totalAReceber = 0;
  totalRecebidos = 0;
  loadingContas = false;

  // Filtros independentes por secao (replica filtroContaPagar/filtroContaReceber do mobile)
  contaSelecionadaPagar: ContaDTO | null = null;
  periodoSelecionadoPagar: PeriodoFiltro | null = null;
  contaSelecionadaReceber: ContaDTO | null = null;
  periodoSelecionadoReceber: PeriodoFiltro | null = null;

  // Paginacao independente por card
  paginaAtualPagar = 0;
  paginaAtualReceber = 0;

  // Grafico de linha
  anoGraficoLinha: number = new Date().getFullYear();
  dadosReceitasLinha: number[] = new Array(12).fill(0);
  dadosDespesasLinha: number[] = new Array(12).fill(0);
  loadingGraficoLinha = false;

  // Graficos de pizza
  dadosDespesasPizza: DadosGraficoPizza[] = [];
  dadosReceitasPizza: DadosGraficoPizza[] = [];
  totalDespesasPizza = 0;
  totalReceitasPizza = 0;
  loadingPizza = false;

  // Minhas contas
  loadingMinhasContas = false;

  // Estoque limitado
  produtosEstoqueLimitado: ProdutosDTO[] = [];
  loadingEstoque = false;

  // Flags de visibilidade
  get exibirPrimeirosPassos(): boolean {
    return !!this.configuracaoEmpresa && !this.configuracaoEmpresa.concluiuOsPrimeirosPassos;
  }

  get exibirFaturaEletronica(): boolean {
    return this.mostrarPassosFaturaEletronica;
  }

  get exibirAvisos(): boolean {
    return this.avisos.length > 0;
  }

  // Estoque limitado exige a janela ITENS, como no dashboard do mobile: sem ela a
  // secao some em vez de listar produtos que a pessoa nao pode abrir.
  get exibirEstoque(): boolean {
    return this.permissoes.produto && this.produtosEstoqueLimitado.length > 0;
  }

  get exibirMinhasContas(): boolean {
    return this.contas.length > 0;
  }

  // =========================================================================
  // Banner de renovacao (plano pago vencido)
  // =========================================================================

  // Verdadeiro quando o plano contratado (diferente do gratuito/Start) ja
  // passou da data de vencimento. Usado para exibir o banner de renovacao.
  get planoPagoVencido(): boolean {
    const inscricao = this.configuracaoEmpresa?.inscricaoAtual;
    const plano = inscricao?.plano;
    if (!plano || plano.planoStart) return false;
    const vencimento = this.parsearData(inscricao?.dataDeCancelamento);
    if (!vencimento) return false;
    const hoje = new Date();
    hoje.setHours(0, 0, 0, 0);
    vencimento.setHours(0, 0, 0, 0);
    return hoje.getTime() > vencimento.getTime();
  }

  // Dias de carencia restantes para renovar (de 1 ate 5). Zero quando a
  // carencia ja terminou.
  get diasRestantesCarencia(): number {
    const vencimento = this.parsearData(
      this.configuracaoEmpresa?.inscricaoAtual?.dataDeCancelamento);
    if (!vencimento) return 0;
    const hoje = new Date();
    hoje.setHours(0, 0, 0, 0);
    vencimento.setHours(0, 0, 0, 0);
    const diasDesdeVencimento = Math.floor(
      (hoje.getTime() - vencimento.getTime()) / (1000 * 60 * 60 * 24));
    const restantes = 5 - diasDesdeVencimento + 1;
    return Math.min(Math.max(restantes, 0), 5);
  }

  get dentroDaCarencia(): boolean {
    return this.diasRestantesCarencia > 0;
  }

  get bannerRenovacaoTitulo(): string {
    return this.translate.instant('planos.banner_renovacao_titulo');
  }

  get bannerRenovacaoMensagem(): string {
    return this.dentroDaCarencia
      ? this.translate.instant('planos.banner_renovacao_carencia',
          { dias: this.diasRestantesCarencia })
      : this.translate.instant('planos.banner_renovacao_expirado');
  }

  // Na carencia o usuario renova; apos o vencimento, contrata/assina.
  get bannerRenovacaoAcao(): string {
    return this.dentroDaCarencia
      ? this.translate.instant('planos.banner_renovacao_acao')
      : this.translate.instant('planos.assinar');
  }

  private parsearData(data: string | undefined): Date | null {
    if (!data) return null;
    let d = new Date(data);
    if (isNaN(d.getTime())) {
      const ts = Number(data);
      if (isNaN(ts)) return null;
      d = new Date(ts);
      if (isNaN(d.getTime())) return null;
    }
    return d;
  }

  navegarParaPlanos(): void {
    // Sinaliza para a tela de planos abrir direto a renovacao do plano contratado.
    this.router.navigate(['/planos'], { queryParams: { renovar: true } });
  }

  private langChangeSub!: Subscription;

  constructor(
    private router: Router,
    private translate: TranslateService,
    private messageService: MessageService,
    private totalDashboardService: TotalDashboardService,
    private dashboardService: DashboardService,
    private avisosService: AvisosService,
    private contasPagarReceberService: ContasPagarReceberService,
    private contaService: ContaService,
    private produtoService: ProdutosService,
    private empresaService: EmpresaConfigService,
    private permissaoDashboardService: PermissaoDashboardService,
  ) {}

  ngOnInit(): void {
    this.langChangeSub = this.translate.onLangChange.subscribe(() => {
      // Nao precisa recarregar dados, os sub-componentes gerenciam suas proprias traducoes
    });

    // FASE 1 — Valores padrao
    this.initPeriodoPadrao();
    this.carregarExibirFaturaEletronica();

    // FASE 2 — Carregamento inicial
    this.carregarDadosIniciais();

    // FASE 1b — Carregar permissoes do usuario logado
    this.carregarPermissoes();
  }

  ngOnDestroy(): void {
    if (this.langChangeSub) {
      this.langChangeSub.unsubscribe();
    }
  }

  // =====================================================================
  // FASE 1 — Inicializacao sincrona
  // =====================================================================

  private initPeriodoPadrao(): void {
    // Default alinhado ao app mobile: "Mostrar todos" (showAll) — intervalo fixo
    // 01/01/2000 00:00 -> 31/12/3000 23:59 (ver calcularPeriodoPredefinido).
    const start = new Date(2000, 0, 1, 0, 0);
    const end = new Date(3000, 11, 31, 23, 59);

    const periodoPadrao: PeriodoFiltro = {
      tipo: 'showAll',
      dataInicio: fmtDateTime(start),
      dataFim: fmtDateTime(end),
      texto: this.translate.instant('dashboard.filtros.mostrar_todos') || 'Mostrar todos'
    };

    this.periodoSelecionado = periodoPadrao;
    // Inicializar filtros locais de contas a pagar/receber com o mesmo período padrão
    this.periodoSelecionadoPagar = periodoPadrao;
    this.periodoSelecionadoReceber = periodoPadrao;
  }

  private carregarExibirFaturaEletronica(): void {
    const flag = localStorage.getItem('dashboard_exibir_fatura_eletronica');
    this.mostrarPassosFaturaEletronica = flag !== 'false';
  }

  private carregarPermissoes(): void {
    const email = sessionStorage.getItem('email') || localStorage.getItem('email');
    if (!email) {
      // Sem email na sessao: conceder permissao total (comportamento seguro de fallback)
      this.permissoes = { ...PERMISSAO_TOTAL };
      return;
    }
    this.permissaoDashboardService.buscarPermissoes(email).subscribe({
      next: (permissoes) => {
        this.permissoes = permissoes;
      },
      error: () => {
        this.permissoes = { ...PERMISSAO_TOTAL };
      }
    });
  }

  // Refresh manual de todos os dados (equivalente ao pull-to-refresh do mobile)
  // Preserva os filtros atuais (conta e período) e NÃO seta carregando=true
  // para evitar destruir e recriar a árvore de componentes (efeito de reload completo)
  recarregarTudo(): void {
    this.carregarPermissoes();
    this.carregarDadosFinanceiros();
  }

  // =====================================================================
  // FASE 2 — Carregamento inicial (forkJoin principal)
  // =====================================================================

  private carregarDadosIniciais(): void {
    this.carregando = true;
    this.carregandoFinanceiro = true;
    this.loadingContas = true;
    this.loadingGraficoLinha = true;
    this.loadingPizza = true;

    // FASE 1: Carregar apenas dados gerais (nao-financeiros) + contas + configuracao da empresa.
    // Os dados financeiros (KPIs, graficos, contas a pagar/receber) sao carregados
    // na FASE 2 (carregarDadosFinanceiros) APOS selecionar a conta padrao,
    // para que os valores ja venham filtrados pela conta selecionada.
    forkJoin({
      // === Dados gerais ===
      avisos: this.avisosService.buscarAtivos().pipe(catchError(() => of({ content: [] as AvisoDTO[] }))),
      totalVendasMes: this.dashboardService.getTotalVendasNoMes().pipe(catchError(() => of(0))),
      // Fatura eletronica: so executa se o usuario precisa ver esses passos
      certificadoDigital: this.mostrarPassosFaturaEletronica
        ? this.dashboardService.verificarCertificadoDigital().pipe(catchError(() => of(false)))
        : of(false),
      timbradoEletronico: this.mostrarPassosFaturaEletronica
        ? this.dashboardService.verificarTimbradoEletronico().pipe(catchError(() => of(false)))
        : of(false),
      vendaTimbrado: this.mostrarPassosFaturaEletronica
        ? this.dashboardService.verificarVendaComTimbradoEletronico().pipe(catchError(() => of(false)))
        : of(false),
      temProdutos: this.dashboardService.verificarTemProdutos().pipe(catchError(() => of(false))),
      temClientes: this.dashboardService.verificarTemClientes().pipe(catchError(() => of(false))),
      temVendas: this.dashboardService.verificarTemVendas().pipe(catchError(() => of(false))),
      contas: this.contaService.get<ContaResponseDTO>('/conta', {}).pipe(catchError(() => of({ content: [] as ContaDTO[] }))),
      estoqueLimitado: this.produtoService.listarItensSaldoMenorIgualEstoque().pipe(catchError(() => of([] as ProdutosDTO[]))),

      // === Cadeia com dependencia real (getSessao → getConfiguracaoEmpresa) ===
      configuracaoEmpresa: this.empresaService.getSessao().pipe(
        switchMap((sessao) => {
          const empresaId = sessao?.id || sessao?.empresaId || 0;
          this.empresaId = empresaId;
          return empresaId
            ? this.dashboardService.getConfiguracaoEmpresa(empresaId).pipe(catchError(() => of(undefined)))
            : of(undefined);
        }),
        catchError(() => of(undefined))
      )
    }).subscribe({
      next: (result) => {
        // === Distribuir dados gerais ===
        this.configuracaoEmpresa = result.configuracaoEmpresa as ConfiguracaoEmpresaDashboardDTO | undefined;
        this.avisos = result.avisos.content || [];
        this.totalVendasNoMes = result.totalVendasMes as number;
        this.temCertificadoDigital = result.certificadoDigital as boolean;
        this.temTimbradoEletronico = result.timbradoEletronico as boolean;
        this.temVendaTimbrado = result.vendaTimbrado as boolean;
        this.temProduto = result.temProdutos as boolean;
        this.temCliente = result.temClientes as boolean;
        this.temVenda = result.temVendas as boolean;
        this.contas = result.contas.content || [];
        this.produtosEstoqueLimitado = result.estoqueLimitado as ProdutosDTO[];

        // Ordenar: conta DINHEIRO da moeda padrao da empresa aparece primeiro
        const moedaPadraoSort = this.configuracaoEmpresa?.moeda?.enum;
        if (moedaPadraoSort) {
          const moedaPadrao = String(moedaPadraoSort).toUpperCase();
          this.contas.sort((a, b) => {
            const tipoA = this.extractEnumString(a.tipoConta);
            const tipoB = this.extractEnumString(b.tipoConta);
            const moedaA = this.extractEnumString(a.moeda);
            const moedaB = this.extractEnumString(b.moeda);
            const isTopA = tipoA === 'DINHEIRO' && moedaA === moedaPadrao ? 1 : 0;
            const isTopB = tipoB === 'DINHEIRO' && moedaB === moedaPadrao ? 1 : 0;
            return isTopB - isTopA;
          });
        }

        // Auto-selecionar a conta DINHEIRO da moeda padrao da empresa.
        // Isso garante que os KPIs ja venham filtrados por conta desde o inicio,
        // mostrando valores consistentes com as paginas de movimentacoes e receitas.
        const moedaEmpresa = this.configuracaoEmpresa?.moeda?.enum;
        const contaInicial = moedaEmpresa
          ? (this.contas.find(c => this.extrairMoedaEnum(c.moeda) === moedaEmpresa) ?? this.contas[0] ?? null)
          : (this.contas[0] ?? null);

        if (contaInicial) {
          this.contaSelecionada = contaInicial;
          this.contaSelecionadaPagar = contaInicial;
          this.contaSelecionadaReceber = contaInicial;
          this.atualizarMoedaPorConta(contaInicial);
          this.atualizarMoedaPagar(contaInicial);
          this.atualizarMoedaReceber(contaInicial);
        } else if (this.contas.length === 0) {
          // Fallback: sem contas, usar moeda da empresa
          this.configurarMoeda();
        }

        // Verificar dados da empresa para fatura eletronica
        this.temDadosEmpresa = !!(this.configuracaoEmpresa?.ruc && this.configuracaoEmpresa?.endereco);

        // Verificar conclusao primeiros passos
        this.verificarConclusaoPrimeirosPassos();

        // Remover loading global (dados gerais ja carregados, tela ja pode ser exibida)
        this.carregando = false;

        // FASE 2: Carregar dados financeiros com a conta ja selecionada (uma unica vez)
        this.carregarDadosFinanceiros();
      },
      error: () => {
        this.carregando = false;
        this.carregandoFinanceiro = false;
        this.loadingContas = false;
        this.loadingGraficoLinha = false;
        this.loadingPizza = false;
        this.messageService.add({
          severity: 'error',
          summary: this.translate.instant('mensagens.erro') || 'Erro ao carregar dashboard'
        });
      }
    });
  }

  // =====================================================================
  // FASE 3 — Dados financeiros (forkJoin secundario)
  // =====================================================================

  private carregarDadosFinanceiros(): void {
    this.carregandoFinanceiro = true;
    this.loadingContas = true;
    this.loadingGraficoLinha = true;
    this.loadingPizza = true;

    const anoAtual = new Date().getFullYear();
    const contaId = this.contaSelecionada?.id;

    // Calcular datas no formato dd/MM/yyyy para DRE
    const dreDatas = this.calcularDatasParaDre(new Date().getMonth() + 1, anoAtual);

    forkJoin({
      // KPIs do cabecalho — identico ao mobile /totaldashboard/total-geral
      totalGeral: this.totalDashboardService.getTotalGeral({
        dataHoraInicial: this.periodoSelecionado.dataInicio,
        dataHoraFinal: this.periodoSelecionado.dataFim,
        conta: contaId
      }).pipe(catchError(() => of({ totalDeEntradas: 0, totalDeSaidas: 0, totalEntradasESaidas: 0 } as Partial<TotalDashboard>))),
      // KPIs do mes anterior (para calculo de tendencia "vs mes anterior")
      totalGeralMesAnterior: this.buscarTotalMesAnterior(contaId),
      graficoLinha: this.totalDashboardService.getTotalsPorMes(anoAtual, anoAtual).pipe(
        catchError(() => of([] as TotalDashboard[]))
      ),
      dadosDre: this.dashboardService.getDadosDre({
        dataInicial: dreDatas.dataInicial,
        dataFinal: dreDatas.dataFinal,
        filtroMoeda: this.configuracaoEmpresa?.moeda?.enum
      }).pipe(catchError(() => of([] as DreItemDTO[]))),
      contasPagar: this.contasPagarReceberService.buscarContasAgrupadas({
        page: 0,
        tipoOperacaoFinanceira: 'SAIDA',
        dataHoraInicial: this.periodoSelecionado.dataInicio,
        dataHoraFinal: this.periodoSelecionado.dataFim,
        conta: contaId
      }).pipe(catchError(() => of({ content: [] as ContaPagarReceberDTO[] }))),
      contasReceber: this.contasPagarReceberService.buscarContasAgrupadas({
        page: 0,
        tipoOperacaoFinanceira: 'ENTRADA',
        dataHoraInicial: this.periodoSelecionado.dataInicio,
        dataHoraFinal: this.periodoSelecionado.dataFim,
        conta: contaId
      }).pipe(catchError(() => of({ content: [] as ContaPagarReceberDTO[] }))),
      totaisContas: this.contasPagarReceberService.buscarTotais({
        dataHoraInicial: this.periodoSelecionado.dataInicio,
        dataHoraFinal: this.periodoSelecionado.dataFim,
        tipoOperacaoFinanceira: 'ENTRADA',
        conta: contaId
      }).pipe(catchError(() => of(null))),
      totaisContasPagar: this.contasPagarReceberService.buscarTotais({
        dataHoraInicial: this.periodoSelecionado.dataInicio,
        dataHoraFinal: this.periodoSelecionado.dataFim,
        tipoOperacaoFinanceira: 'SAIDA',
        conta: contaId
      }).pipe(catchError(() => of(null)))
    }).subscribe({
      next: (result) => {
        // KPIs do cabecalho (replica getDadosReceitaDespesa do mobile)
        const totalGeral = result.totalGeral as Partial<TotalDashboard>;
        this.totalReceita = totalGeral?.totalDeEntradas || 0;
        this.totalDespesa = totalGeral?.totalDeSaidas || 0;
        this.totalSaldo = totalGeral?.totalEntradasESaidas || 0;

        // Tendencia "vs mes anterior"
        this.periodoEhMesFechado = this.verificarMesFechado(this.periodoSelecionado);
        const totalMesAnt = result.totalGeralMesAnterior as Partial<TotalDashboard>;
        this.totalReceitaMesAnterior = totalMesAnt?.totalDeEntradas || 0;
        this.totalDespesaMesAnterior = totalMesAnt?.totalDeSaidas || 0;
        this.totalSaldoMesAnterior = totalMesAnt?.totalEntradasESaidas || 0;

        // Grafico de linha
        this.processarGraficoLinha(result.graficoLinha as TotalDashboard[]);
        this.loadingGraficoLinha = false;

        // Graficos de pizza (DRE)
        this.processarDadosDre(result.dadosDre as DreItemDTO[]);
        this.loadingPizza = false;

        // Contas a pagar/receber
        this.contasAPagar = result.contasPagar.content || [];
        this.contasAReceber = result.contasReceber.content || [];

        const moedaReload = this.contaSelecionada?.moeda || Moeda.GUARANI;
        const totaisContas = result.totaisContas as TotaisContasDTO | null;
        if (totaisContas) {
          this.totalAReceber = this.extrairValorPorMoeda(totaisContas.totalContaAReceberAbertas, moedaReload);
          this.totalRecebidos = this.extrairValorPorMoeda(totaisContas.totalContaAReceberRecebidas, moedaReload);
        }
        const totaisContasPagar = result.totaisContasPagar as TotaisContasDTO | null;
        if (totaisContasPagar) {
          this.totalAPagar = this.extrairValorPorMoeda(totaisContasPagar.totalContaAPagarAbertas, moedaReload);
          this.totalPagos = this.extrairValorPorMoeda(totaisContasPagar.totalContaAPagarPagas, moedaReload);
        }
        this.loadingContas = false;

        this.carregandoFinanceiro = false;
      },
      error: () => {
        this.carregandoFinanceiro = false;
        this.loadingContas = false;
        this.loadingGraficoLinha = false;
        this.loadingPizza = false;
      }
    });
  }

  // =====================================================================
  // FASE 4 — Verificar e concluir primeiros passos
  // =====================================================================

  private verificarConclusaoPrimeirosPassos(): void {
    if (this.temProduto && this.temCliente && this.temVenda
        && this.configuracaoEmpresa && !this.configuracaoEmpresa.concluiuOsPrimeirosPassos) {
      this.dashboardService.concluirPrimeirosPassos().subscribe({
        next: () => {
          if (this.configuracaoEmpresa) {
            this.configuracaoEmpresa.concluiuOsPrimeirosPassos = true;
          }
        },
        error: () => { /* silencioso */ }
      });
    }
  }

  // =====================================================================
  // Eventos dos sub-componentes
  // =====================================================================

  onPeriodoAlterado(novoPeriodo: PeriodoFiltro): void {
    this.periodoSelecionado = novoPeriodo;
    this.recarregarDadosFiltrados();
  }

  onContaAlterada(conta: ContaDTO | null): void {
    this.contaSelecionada = conta;
    // Sincronizar filtros locais de contas a pagar/receber com a conta do header
    this.contaSelecionadaPagar = conta;
    this.contaSelecionadaReceber = conta;
    if (conta) {
      this.atualizarMoedaPorConta(conta);
      this.atualizarMoedaPagar(conta);
      this.atualizarMoedaReceber(conta);
    }
    this.recarregarDadosFiltrados();
  }

  // Filtros independentes da secao de contas a pagar
  onContaPagarAlterada(conta: ContaDTO | null): void {
    this.contaSelecionadaPagar = conta;
    if (conta) {
      this.atualizarMoedaPagar(conta);
    }
    this.paginaAtualPagar = 0;
    this.recarregarContasPagar(0, false);
  }

  onPeriodoPagarAlterado(periodo: PeriodoFiltro): void {
    this.periodoSelecionadoPagar = periodo;
    this.paginaAtualPagar = 0;
    this.recarregarContasPagar(0, false);
  }

  // Filtros independentes da secao de contas a receber
  onContaReceberAlterada(conta: ContaDTO | null): void {
    this.contaSelecionadaReceber = conta;
    if (conta) {
      this.atualizarMoedaReceber(conta);
    }
    this.paginaAtualReceber = 0;
    this.recarregarContasReceber(0, false);
  }

  onPeriodoReceberAlterado(periodo: PeriodoFiltro): void {
    this.periodoSelecionadoReceber = periodo;
    this.paginaAtualReceber = 0;
    this.recarregarContasReceber(0, false);
  }

  // Handlers de "Mostrar mais" — incrementam pagina e acumulam resultados
  onMostrarMaisPagar(): void {
    this.paginaAtualPagar += 1;
    this.recarregarContasPagar(this.paginaAtualPagar, true);
  }

  onMostrarMaisReceber(): void {
    this.paginaAtualReceber += 1;
    this.recarregarContasReceber(this.paginaAtualReceber, true);
  }

  onAnoGraficoAlterado(ano: number): void {
    this.anoGraficoLinha = ano;
    this.loadingGraficoLinha = true;
    this.totalDashboardService.getTotalsPorMes(ano, ano).pipe(
      catchError(() => of([] as TotalDashboard[]))
    ).subscribe({
      next: (data) => {
        this.processarGraficoLinha(data);
        this.loadingGraficoLinha = false;
      },
      error: () => {
        this.loadingGraficoLinha = false;
      }
    });
  }

  onFiltroPizzaAlterado(filtro: { ano: number; mes: number | null; moeda: string }): void {
    this.loadingPizza = true;
    const datas = this.calcularDatasParaDre(filtro.mes, filtro.ano);
    this.dashboardService.getDadosDre({
      dataInicial: datas.dataInicial,
      dataFinal: datas.dataFinal,
      filtroMoeda: filtro.moeda
    }).pipe(
      catchError(() => of([] as DreItemDTO[]))
    ).subscribe({
      next: (dados) => {
        this.processarDadosDre(dados);
        this.loadingPizza = false;
      },
      error: () => {
        this.loadingPizza = false;
      }
    });
  }

  onFaturaEletronicaDismiss(): void {
    this.mostrarPassosFaturaEletronica = false;
    localStorage.setItem('dashboard_exibir_fatura_eletronica', 'false');
  }

  // Drill-down: navega para lista de movimentos de despesa filtrada por tipo
  // Replica grafico_pizza_widget.dart onTap com tipoDespesa (linhas ~272-280)
  onDrillDownDespesa(evento: { tipoEnum: string; nome: string; dataInicial: string; dataFinal: string; moeda: string }): void {
    if (!this.permissoes.gastos) return;
    this.router.navigate(['/nova-despesa'], {
      queryParams: {
        tipoDespesa: evento.tipoEnum,
        dataInicial: evento.dataInicial,
        dataFinal: evento.dataFinal,
        moeda: evento.moeda
      }
    });
  }

  // Drill-down: navega para lista de movimentos de receita filtrada por tipo
  // Replica grafico_pizza_widget.dart onTap com tipoReceita (linhas ~208-216)
  onDrillDownReceita(evento: { tipoEnum: string; nome: string; dataInicial: string; dataFinal: string; moeda: string }): void {
    if (!this.permissoes.receitas) return;
    this.router.navigate(['/nova-receita'], {
      queryParams: {
        tipoReceita: evento.tipoEnum,
        dataInicial: evento.dataInicial,
        dataFinal: evento.dataFinal,
        moeda: evento.moeda
      }
    });
  }

  // Navega para contas a pagar com os filtros ativos no card do dashboard
  onNavContasPagar(tipo: 'aberto' | 'pago'): void {
    if (!this.permissoes.conta) return;
    const periodo = this.periodoSelecionadoPagar || this.periodoSelecionado;
    const contaId = this.contaSelecionadaPagar?.id;
    const queryParams: Record<string, any> = {
      tipo: 'SAIDA',
      situacao: tipo === 'aberto' ? 'ABERTO' : 'PAGO',
      dataInicio: periodo.dataInicio,
      dataFim: periodo.dataFim,
    };
    if (contaId !== undefined && contaId !== null) {
      queryParams['contaId'] = contaId;
    }
    this.router.navigate(['/contas-pagar-receber'], { queryParams });
  }

  // Navega para contas a receber com os filtros ativos no card do dashboard
  onNavContasReceber(tipo: 'aberto' | 'pago'): void {
    if (!this.permissoes.conta) return;
    const periodo = this.periodoSelecionadoReceber || this.periodoSelecionado;
    const contaId = this.contaSelecionadaReceber?.id;
    const queryParams: Record<string, any> = {
      tipo: 'ENTRADA',
      situacao: tipo === 'aberto' ? 'ABERTO' : 'PAGO',
      dataInicio: periodo.dataInicio,
      dataFim: periodo.dataFim,
    };
    if (contaId !== undefined && contaId !== null) {
      queryParams['contaId'] = contaId;
    }
    this.router.navigate(['/contas-pagar-receber'], { queryParams });
  }

  // Navega para titulos da pessoa ao clicar numa conta a receber (sem datas, igual mobile)
  onContaReceberClicada(conta: ContaPagarReceberDTO): void {
    if (!this.permissoes.conta) return;
    this.router.navigate(['/contas-a-receber/pessoa', conta.idPessoa], {
      queryParams: { nome: conta.nomePessoa },
    });
  }

  // Navega para titulos da pessoa ao clicar numa conta a pagar (sem datas, igual mobile)
  onContaPagarClicada(conta: ContaPagarReceberDTO): void {
    if (!this.permissoes.conta) return;
    this.router.navigate(['/contas-a-pagar/pessoa', conta.idPessoa], {
      queryParams: { nome: conta.nomePessoa, tipo: 'SAIDA' },
    });
  }

  // =====================================================================
  // Recarregar lista de contas (chamado apos criar ou excluir conta)
  // =====================================================================

  recarregarContas(): void {
    this.loadingMinhasContas = true;
    this.contaService.get<ContaResponseDTO>('/conta', {}).pipe(
      catchError(() => of({ content: [] as ContaDTO[] }))
    ).subscribe({
      next: (result) => {
        this.contas = result.content || [];

        // Ordenar: conta DINHEIRO da moeda padrao da empresa aparece primeiro
        const moedaEmpresa = this.configuracaoEmpresa?.moeda?.enum;
        if (moedaEmpresa) {
          const moedaPadrao = String(moedaEmpresa).toUpperCase();
          this.contas.sort((a, b) => {
            const tipoA = this.extractEnumString(a.tipoConta);
            const tipoB = this.extractEnumString(b.tipoConta);
            const moedaA = this.extractEnumString(a.moeda);
            const moedaB = this.extractEnumString(b.moeda);
            const isTopA = tipoA === 'DINHEIRO' && moedaA === moedaPadrao ? 1 : 0;
            const isTopB = tipoB === 'DINHEIRO' && moedaB === moedaPadrao ? 1 : 0;
            return isTopB - isTopA;
          });
        }

        this.loadingMinhasContas = false;
      },
      error: () => {
        this.loadingMinhasContas = false;
      }
    });
  }

  /** Extrai string do enum, seja objeto { enum: 'X' } ou string direta */
  private extractEnumString(v: any): string {
    if (!v) return '';
    if (typeof v === 'object' && 'enum' in v) return String(v.enum).toUpperCase();
    if (typeof v === 'object' && 'value' in v) return String(v.value).toUpperCase();
    return String(v ?? '').toUpperCase();
  }

  // =====================================================================
  // Recarregar dados filtrados
  // =====================================================================

  private recarregarDadosFiltrados(): void {
    const contaId = this.contaSelecionada?.id;

    // Atualizar KPIs do cabecalho + mes anterior para tendencia
    this.periodoEhMesFechado = this.verificarMesFechado(this.periodoSelecionado);

    forkJoin({
      totalGeral: this.totalDashboardService.getTotalGeral({
        dataHoraInicial: this.periodoSelecionado.dataInicio,
        dataHoraFinal: this.periodoSelecionado.dataFim,
        conta: contaId
      }).pipe(catchError(() => of({ totalDeEntradas: 0, totalDeSaidas: 0, totalEntradasESaidas: 0 } as Partial<TotalDashboard>))),
      totalGeralMesAnterior: this.buscarTotalMesAnterior(contaId)
    }).subscribe({
      next: (result) => {
        const totalGeral = result.totalGeral as Partial<TotalDashboard>;
        this.totalReceita = totalGeral?.totalDeEntradas || 0;
        this.totalDespesa = totalGeral?.totalDeSaidas || 0;
        this.totalSaldo = totalGeral?.totalEntradasESaidas || 0;

        const totalMesAnt = result.totalGeralMesAnterior as Partial<TotalDashboard>;
        this.totalReceitaMesAnterior = totalMesAnt?.totalDeEntradas || 0;
        this.totalDespesaMesAnterior = totalMesAnt?.totalDeSaidas || 0;
        this.totalSaldoMesAnterior = totalMesAnt?.totalEntradasESaidas || 0;
      }
    });

    // Reiniciar paginacao
    this.paginaAtualPagar = 0;
    this.paginaAtualReceber = 0;

    // Verificar se os filtros de pagar e receber sao identicos
    // para evitar chamada duplicada de buscarTotais
    const periodoPagar = this.periodoSelecionadoPagar || this.periodoSelecionado;
    const periodoReceber = this.periodoSelecionadoReceber || this.periodoSelecionado;
    const contaIdPagar = this.contaSelecionadaPagar?.id;
    const contaIdReceber = this.contaSelecionadaReceber?.id;

    const filtrosIguais =
      periodoPagar.dataInicio === periodoReceber.dataInicio &&
      periodoPagar.dataFim === periodoReceber.dataFim &&
      contaIdPagar === contaIdReceber;

    if (filtrosIguais) {
      // Filtros identicos: fazer os 3 forkJoin em paralelo com totais compartilhados
      this.recarregarContasPagarEReceberParalelo(periodoPagar, contaIdPagar);
    } else {
      // Filtros diferentes: chamadas independentes (cada uma com seus proprios totais)
      this.recarregarContasPagar();
      this.recarregarContasReceber();
    }
  }

  // =====================================================================
  // Recarregar pagar e receber em paralelo com totais compartilhados
  // Evita chamada duplicada de buscarTotais quando filtros sao identicos
  // =====================================================================

  private recarregarContasPagarEReceberParalelo(
    periodo: PeriodoFiltro,
    contaId: number | undefined
  ): void {
    const moedaPagar = this.contaSelecionadaPagar?.moeda || this.contaSelecionada?.moeda || Moeda.GUARANI;
    const moedaReceber = this.contaSelecionadaReceber?.moeda || this.contaSelecionada?.moeda || Moeda.GUARANI;

    this.loadingContas = true;

    forkJoin({
      contasPagar: this.contasPagarReceberService.buscarContasAgrupadas({
        page: 0,
        tipoOperacaoFinanceira: 'SAIDA',
        dataHoraInicial: periodo.dataInicio,
        dataHoraFinal: periodo.dataFim,
        conta: contaId
      }).pipe(catchError(() => of({ content: [] as ContaPagarReceberDTO[] }))),
      contasReceber: this.contasPagarReceberService.buscarContasAgrupadas({
        page: 0,
        tipoOperacaoFinanceira: 'ENTRADA',
        dataHoraInicial: periodo.dataInicio,
        dataHoraFinal: periodo.dataFim,
        conta: contaId
      }).pipe(catchError(() => of({ content: [] as ContaPagarReceberDTO[] }))),
      totais: this.contasPagarReceberService.buscarTotais({
        dataHoraInicial: periodo.dataInicio,
        dataHoraFinal: periodo.dataFim,
        tipoOperacaoFinanceira: 'ENTRADA',
        conta: contaId
      }).pipe(catchError(() => of(null))),
      totaisPagar: this.contasPagarReceberService.buscarTotais({
        dataHoraInicial: periodo.dataInicio,
        dataHoraFinal: periodo.dataFim,
        tipoOperacaoFinanceira: 'SAIDA',
        conta: contaId
      }).pipe(catchError(() => of(null)))
    }).subscribe({
      next: (result) => {
        this.contasAPagar = result.contasPagar.content || [];
        this.contasAReceber = result.contasReceber.content || [];

        if (result.totais) {
          const totaisContas = result.totais as TotaisContasDTO;
          this.totalAReceber = this.extrairValorPorMoeda(totaisContas.totalContaAReceberAbertas, moedaReceber);
          this.totalRecebidos = this.extrairValorPorMoeda(totaisContas.totalContaAReceberRecebidas, moedaReceber);
        }
        if (result.totaisPagar) {
          const totaisPagar = result.totaisPagar as TotaisContasDTO;
          this.totalAPagar = this.extrairValorPorMoeda(totaisPagar.totalContaAPagarAbertas, moedaPagar);
          this.totalPagos = this.extrairValorPorMoeda(totaisPagar.totalContaAPagarPagas, moedaPagar);
        }
        this.loadingContas = false;
      },
      error: () => { this.loadingContas = false; }
    });
  }

  // =====================================================================
  // Recarregar apenas contas a pagar (filtro independente)
  // =====================================================================

  private recarregarContasPagar(pagina = 0, acumular = false): void {
    const periodo = this.periodoSelecionadoPagar || this.periodoSelecionado;
    const contaId = this.contaSelecionadaPagar?.id;
    const moeda = this.contaSelecionadaPagar?.moeda || this.contaSelecionada?.moeda || Moeda.GUARANI;

    this.loadingContas = true;

    forkJoin({
      contas: this.contasPagarReceberService.buscarContasAgrupadas({
        page: pagina,
        tipoOperacaoFinanceira: 'SAIDA',
        dataHoraInicial: periodo.dataInicio,
        dataHoraFinal: periodo.dataFim,
        conta: contaId
      }).pipe(catchError(() => of({ content: [] as ContaPagarReceberDTO[] }))),
      totais: pagina === 0
        ? this.contasPagarReceberService.buscarTotais({
            dataHoraInicial: periodo.dataInicio,
            dataHoraFinal: periodo.dataFim,
            tipoOperacaoFinanceira: 'SAIDA',
            conta: contaId
          }).pipe(catchError(() => of(null)))
        : of(null)
    }).subscribe({
      next: (result) => {
        const novasContas = result.contas.content || [];
        this.contasAPagar = acumular
          ? [...this.contasAPagar, ...novasContas]
          : novasContas;

        if (result.totais) {
          const totaisContas = result.totais as TotaisContasDTO;
          this.totalAPagar = this.extrairValorPorMoeda(totaisContas.totalContaAPagarAbertas, moeda);
          this.totalPagos = this.extrairValorPorMoeda(totaisContas.totalContaAPagarPagas, moeda);
        }
        this.loadingContas = false;
      },
      error: () => { this.loadingContas = false; }
    });
  }

  // =====================================================================
  // Recarregar apenas contas a receber (filtro independente)
  // =====================================================================

  private recarregarContasReceber(pagina = 0, acumular = false): void {
    const periodo = this.periodoSelecionadoReceber || this.periodoSelecionado;
    const contaId = this.contaSelecionadaReceber?.id;
    const moeda = this.contaSelecionadaReceber?.moeda || this.contaSelecionada?.moeda || Moeda.GUARANI;

    this.loadingContas = true;

    forkJoin({
      contas: this.contasPagarReceberService.buscarContasAgrupadas({
        page: pagina,
        tipoOperacaoFinanceira: 'ENTRADA',
        dataHoraInicial: periodo.dataInicio,
        dataHoraFinal: periodo.dataFim,
        conta: contaId
      }).pipe(catchError(() => of({ content: [] as ContaPagarReceberDTO[] }))),
      totais: pagina === 0
        ? this.contasPagarReceberService.buscarTotais({
            dataHoraInicial: periodo.dataInicio,
            dataHoraFinal: periodo.dataFim,
            tipoOperacaoFinanceira: 'ENTRADA',
            conta: contaId
          }).pipe(catchError(() => of(null)))
        : of(null)
    }).subscribe({
      next: (result) => {
        const novasContas = result.contas.content || [];
        this.contasAReceber = acumular
          ? [...this.contasAReceber, ...novasContas]
          : novasContas;

        if (result.totais) {
          const totaisContas = result.totais as TotaisContasDTO;
          this.totalAReceber = this.extrairValorPorMoeda(totaisContas.totalContaAReceberAbertas, moeda);
          this.totalRecebidos = this.extrairValorPorMoeda(totaisContas.totalContaAReceberRecebidas, moeda);
        }
        this.loadingContas = false;
      },
      error: () => { this.loadingContas = false; }
    });
  }

  // =====================================================================
  // Processamento de dados
  // =====================================================================

  private processarGraficoLinha(data: TotalDashboard[]): void {
    const receitas = new Array(12).fill(0);
    const despesas = new Array(12).fill(0);

    (data || []).forEach(d => {
      const mesIndex = (d.mesBusca || 1) - 1;
      if (mesIndex >= 0 && mesIndex < 12) {
        receitas[mesIndex] = d.totalDeEntradas || 0;
        despesas[mesIndex] = d.totalDeSaidas || 0;
      }
    });

    this.dadosReceitasLinha = receitas;
    this.dadosDespesasLinha = despesas;
  }

  private processarDadosDre(dados: DreItemDTO[]): void {
    const receitasMap = new Map<string, { nome: string; tipoEnum: string; valor: number }>();
    const despesasMap = new Map<string, { nome: string; tipoEnum: string; valor: number }>();

    (dados || []).forEach(item => {
      if (item.tipoReceita) {
        const key = item.tipoReceita.enum;
        const existing = receitasMap.get(key) || { nome: item.tipoReceita.nome, tipoEnum: key, valor: 0 };
        existing.valor += item.valor || 0;
        receitasMap.set(key, existing);
      }
      if (item.tipoDespesa) {
        const key = item.tipoDespesa.enum;
        const existing = despesasMap.get(key) || { nome: item.tipoDespesa.nome, tipoEnum: key, valor: 0 };
        existing.valor += item.valor || 0;
        despesasMap.set(key, existing);
      }
    });

    this.dadosReceitasPizza = this.mapToPizzaData(receitasMap);
    this.dadosDespesasPizza = this.mapToPizzaData(despesasMap);

    this.totalReceitasPizza = this.dadosReceitasPizza.reduce((s, d) => s + d.valor, 0);
    this.totalDespesasPizza = this.dadosDespesasPizza.reduce((s, d) => s + d.valor, 0);
  }

  private mapToPizzaData(map: Map<string, { nome: string; tipoEnum: string; valor: number }>): DadosGraficoPizza[] {
    const sorted = Array.from(map.values()).sort((a, b) => b.valor - a.valor);
    const total = sorted.reduce((s, d) => s + d.valor, 0);

    let resultado: DadosGraficoPizza[];

    if (sorted.length < 8) {
      // Menos de 8 itens: exibir todos sem agrupar
      resultado = sorted.map((item, index) => ({
        nome: item.nome,
        tipoEnum: item.tipoEnum,
        valor: item.valor,
        percentual: total > 0 ? (item.valor / total) * 100 : 0,
        cor: PALETA_CORES[index % PALETA_CORES.length]
      }));
    } else {
      // 8 ou mais itens: top 7 + "Outros" (igual ao mobile)
      const top7 = sorted.slice(0, 7);
      const outros = sorted.slice(7);
      const outrosValor = outros.reduce((s, d) => s + d.valor, 0);

      resultado = top7.map((item, index) => ({
        nome: item.nome,
        tipoEnum: item.tipoEnum,
        valor: item.valor,
        percentual: total > 0 ? (item.valor / total) * 100 : 0,
        cor: PALETA_CORES[index % PALETA_CORES.length]
      }));

      resultado.push({
        nome: this.translate.instant('dashboard.texts.outros') || 'Outros',
        tipoEnum: 'OUTROS',
        valor: outrosValor,
        percentual: total > 0 ? (outrosValor / total) * 100 : 0,
        cor: PALETA_CORES[7 % PALETA_CORES.length]
      });
    }

    return resultado;
  }

  // =====================================================================
  // Multimoeda — extrai o valor correto conforme a moeda da conta selecionada
  // Replica a logica do mobile: switch na moeda da conta (dashboard_screen.dart linhas ~469-503)
  // =====================================================================

  private extrairValorPorMoeda(valorPorMoeda: ValorPorMoedaDTO | undefined, moeda: Moeda | any): number {
    if (!valorPorMoeda) return 0;
    const moedaEnum = this.extrairMoedaEnum(moeda);
    switch (moedaEnum) {
      case Moeda.REAL:
        return valorPorMoeda.valorTotalEmReal || 0;
      case Moeda.DOLAR:
        return valorPorMoeda.valorTotalEmDolar || 0;
      case Moeda.GUARANI:
      default:
        return valorPorMoeda.valorTotalEmGuarani || 0;
    }
  }

  // =====================================================================
  // Moeda
  // =====================================================================

  // O backend serializa enums via LocalizableEnumSerializer como objeto { enum, nome, ... }
  // Este metodo extrai o valor do enum independente do formato recebido
  private extrairMoedaEnum(moeda: any): Moeda {
    if (!moeda) return Moeda.GUARANI;
    if (typeof moeda === 'string') return moeda as Moeda;
    if (typeof moeda === 'object' && moeda.enum) return moeda.enum as Moeda;
    return Moeda.GUARANI;
  }

  private atualizarMoedaPorConta(conta: ContaDTO): void {
    const moedaEnum = this.extrairMoedaEnum(conta.moeda);
    const config = MOEDA_CONFIG[moedaEnum] || MOEDA_CONFIG[Moeda.GUARANI];
    this.currencySymbol = config.symbol;
    this.currencyDigits = config.digits;
    this.currencyLocale = config.locale;
  }

  private atualizarMoedaPagar(conta: ContaDTO): void {
    const moedaEnum = this.extrairMoedaEnum(conta.moeda);
    const config = MOEDA_CONFIG[moedaEnum] || MOEDA_CONFIG[Moeda.GUARANI];
    this.currencySymbolPagar = config.symbol;
    this.currencyDigitsPagar = config.digits;
    this.currencyLocalePagar = config.locale;
  }

  private atualizarMoedaReceber(conta: ContaDTO): void {
    const moedaEnum = this.extrairMoedaEnum(conta.moeda);
    const config = MOEDA_CONFIG[moedaEnum] || MOEDA_CONFIG[Moeda.GUARANI];
    this.currencySymbolReceber = config.symbol;
    this.currencyDigitsReceber = config.digits;
    this.currencyLocaleReceber = config.locale;
  }

  private configurarMoeda(): void {
    const moedaEnum = (this.configuracaoEmpresa?.moeda?.enum || 'GUARANI') as Moeda;
    const config = MOEDA_CONFIG[moedaEnum] || MOEDA_CONFIG[Moeda.GUARANI];
    this.currencySymbol = config.symbol;
    this.currencyDigits = config.digits;
    this.currencyLocale = config.locale;
  }

  // =====================================================================
  // Tendencia "vs mes anterior"
  // =====================================================================

  /**
   * Verifica se o periodo selecionado corresponde a um mes fechado.
   * Meses fechados: thisMonth, prevMonth, nextMonth, ou meses especificos (tipo numerico 1-12).
   */
  private verificarMesFechado(periodo: PeriodoFiltro): boolean {
    const tipo = periodo.tipo;
    if (tipo === 'thisMonth' || tipo === 'prevMonth' || tipo === 'nextMonth') return true;
    if (typeof tipo === 'number' && tipo >= 1 && tipo <= 12) return true;
    return false;
  }

  /**
   * Calcula as datas do mes anterior ao periodo selecionado e busca os totais.
   * Retorna of(fallback) se o periodo nao for mes fechado.
   */
  private buscarTotalMesAnterior(contaId?: number): Observable<Partial<TotalDashboard>> {
    const fallback = { totalDeEntradas: 0, totalDeSaidas: 0, totalEntradasESaidas: 0 } as Partial<TotalDashboard>;
    const datas = this.calcularPeriodoMesAnterior(this.periodoSelecionado);
    if (!datas) return of(fallback);

    return this.totalDashboardService.getTotalGeral({
      dataHoraInicial: datas.dataInicio,
      dataHoraFinal: datas.dataFim,
      conta: contaId
    }).pipe(catchError(() => of(fallback)));
  }

  /**
   * Retorna as datas do mes anterior ao periodo, ou null se nao for mes fechado.
   */
  private calcularPeriodoMesAnterior(periodo: PeriodoFiltro): { dataInicio: string; dataFim: string } | null {
    if (!this.verificarMesFechado(periodo)) return null;

    const tipo = periodo.tipo;
    const now = new Date();
    let mesRef: number; // 0-indexed
    let anoRef: number;

    if (tipo === 'thisMonth') {
      mesRef = now.getMonth();
      anoRef = now.getFullYear();
    } else if (tipo === 'prevMonth') {
      mesRef = now.getMonth() - 1;
      anoRef = now.getFullYear();
      if (mesRef < 0) { mesRef = 11; anoRef--; }
    } else if (tipo === 'nextMonth') {
      mesRef = now.getMonth() + 1;
      anoRef = now.getFullYear();
      if (mesRef > 11) { mesRef = 0; anoRef++; }
    } else if (typeof tipo === 'number') {
      // Mes especifico (1-12) — assumir ano atual
      mesRef = tipo - 1;
      anoRef = now.getFullYear();
    } else {
      return null;
    }

    // Calcular mes anterior ao mes de referencia
    let mesAnterior = mesRef - 1;
    let anoAnterior = anoRef;
    if (mesAnterior < 0) { mesAnterior = 11; anoAnterior--; }

    const inicio = new Date(anoAnterior, mesAnterior, 1, 0, 0);
    const fim = new Date(anoAnterior, mesAnterior + 1, 0, 23, 59);

    return {
      dataInicio: fmtDateTime(inicio),
      dataFim: fmtDateTime(fim)
    };
  }

  // =====================================================================
  // Helpers de data
  // =====================================================================

  private pad(n: number): string {
    return n < 10 ? ('0' + n) : String(n);
  }

  private calcularDatasParaDre(mes: number | null, ano: number): { dataInicial: string; dataFinal: string } {
    if (mes && mes >= 1 && mes <= 12) {
      const lastDay = new Date(ano, mes, 0).getDate();
      return {
        dataInicial: `01/${this.pad(mes)}/${ano}`,
        dataFinal: `${this.pad(lastDay)}/${this.pad(mes)}/${ano}`
      };
    }
    // Ano completo
    return {
      dataInicial: `01/01/${ano}`,
      dataFinal: `31/12/${ano}`
    };
  }
}
